"use strict";
/**
 * Condition Evaluation Logic
 *
 * This module provides functions to evaluate targeting conditions against a context.
 * Conditions can be attribute-based, segment-based, or flag-based.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.compareValues = compareValues;
exports.evaluateAttributeCondition = evaluateAttributeCondition;
exports.evaluateCondition = evaluateCondition;
const lodash_1 = require("lodash");
/**
 * Compare two values using the specified operator
 */
function compareValues(actual, operator, expected) {
    switch (operator) {
        case "equals":
            return actual === expected;
        case "notEquals":
            return actual !== expected;
        case "contains":
            if (typeof actual === "string" && typeof expected === "string") {
                return actual.includes(expected);
            }
            if (Array.isArray(actual)) {
                return actual.includes(expected);
            }
            return false;
        case "notContains":
            if (typeof actual === "string" && typeof expected === "string") {
                return !actual.includes(expected);
            }
            if (Array.isArray(actual)) {
                return !actual.includes(expected);
            }
            return true;
        case "startsWith":
            if (typeof actual === "string" && typeof expected === "string") {
                return actual.startsWith(expected);
            }
            return false;
        case "endsWith":
            if (typeof actual === "string" && typeof expected === "string") {
                return actual.endsWith(expected);
            }
            return false;
        case "greaterThan":
            if (typeof actual === "number" && typeof expected === "number") {
                return actual > expected;
            }
            return false;
        case "greaterThanOrEquals":
            if (typeof actual === "number" && typeof expected === "number") {
                return actual >= expected;
            }
            return false;
        case "lessThan":
            if (typeof actual === "number" && typeof expected === "number") {
                return actual < expected;
            }
            return false;
        case "lessThanOrEquals":
            if (typeof actual === "number" && typeof expected === "number") {
                return actual <= expected;
            }
            return false;
        case "in":
            if (Array.isArray(expected)) {
                return expected.includes(actual);
            }
            return false;
        case "notIn":
            if (Array.isArray(expected)) {
                return !expected.includes(actual);
            }
            return true;
        case "matches":
            if (typeof actual === "string" && typeof expected === "string") {
                try {
                    const regex = new RegExp(expected);
                    return regex.test(actual);
                }
                catch {
                    return false;
                }
            }
            return false;
        default:
            return false;
    }
}
/**
 * Evaluate an attribute condition against the context
 */
function evaluateAttributeCondition(condition, context) {
    const actualValue = (0, lodash_1.get)(context, condition.attribute);
    return compareValues(actualValue, condition.operator, condition.value);
}
/**
 * Evaluate a condition against the context
 *
 * @param condition - The condition to evaluate
 * @param context - The evaluation context (user + system)
 * @param resolvedFlags - Already resolved flag values (for flag conditions)
 * @param segmentMemberships - Segment membership lookup (for segment conditions)
 * @returns Whether the condition is satisfied
 */
function evaluateCondition(condition, context, resolvedFlags, segmentMemberships) {
    switch (condition.type) {
        case "attribute":
            return evaluateAttributeCondition(condition, context);
        case "segment": {
            const membership = segmentMemberships[condition.segmentId];
            const isMember = membership?.isMember ?? false;
            return condition.negated ? !isMember : isMember;
        }
        case "flag": {
            const flagValue = resolvedFlags[condition.flagKey];
            return compareValues(flagValue, condition.operator, condition.value);
        }
        case "and":
            for (const subCondition of condition.conditions) {
                if (!evaluateCondition(subCondition, context, resolvedFlags, segmentMemberships)) {
                    return false;
                }
            }
            return true;
        case "or":
            for (const subCondition of condition.conditions) {
                if (evaluateCondition(subCondition, context, resolvedFlags, segmentMemberships)) {
                    return true;
                }
            }
            return false;
        default:
            return false;
    }
}
//# sourceMappingURL=conditions.js.map